import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { AccountGetter } from '@/store/account/account.module';
import { Account } from '@/store/account/account.model';
import { Role } from '@/store/role/role.model';
import { EnjoinAction, EnjoinGetter } from '@/store/enjoin/enjoin.module';
import { EnjoinAnswerSchema } from '@/store/enjoin/enjoin.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'EnjoinAnswerFormComnponent',
  components: {
    BaseModalComponent
  },
  props: {
    enjoinId: {
      type: Number,
    },
  },
  setup: (props) => {
    const store = useStore();
    const account = computed<Account | undefined>(() => store.getters[AccountGetter.ACCOUNT]);
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const enjoinAnswerForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      enjoinId?: number | null;
      userId?: string | null;
      userRole?: string | null;
      userName?: string | null;
      description?: string | null;
    }
    const formData = reactive<Form>({
      enjoinId: props.enjoinId,
      userId: account.value?.id,
      userName: `${account.value?.firstName} ${account.value?.lastName}`,
      description: '',
    });
    const validator = computed(() => EnjoinAnswerSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      description: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'description')),
    });
    const isValid = computed(() => !errors.description);
    const isLoading = computed<boolean>(() => store.getters[EnjoinGetter.STATUS] === 'init' || store.getters[EnjoinGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.enjoinId = props.enjoinId;
        formData.userId = account.value?.id;
        formData.userRole = role.value?.name;
        formData.userName = `${account.value?.firstName} ${account.value?.lastName}`;
        resolve();
      })
      .then(() => enjoinAnswerForm.value?.show());
    };

    // hide modal
    const hide = () => {
      enjoinAnswerForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.enjoinId = props.enjoinId;
        formData.userId = account.value?.id;
        formData.userRole = role.value?.name;
        formData.userName = `${account.value?.firstName} ${account.value?.lastName}`;
        formData.description = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        store.dispatch(EnjoinAction.ADD_ANSWER, formData)
          .then(() => hide());
      }
    };

    return {
      account,
      role,
      enjoinAnswerForm,
      formData,
      errors,
      submited,
      isLoading,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
    };
  }
});
